import {
  CheckCircle2,
  Users,
  Brain,
  Clock,
  Shield,
  Factory,
  FlaskConical,
  Truck,
  Building2,
  FileSearch,
  AlertTriangle,
  FileText,
  TrendingUp,
  ShieldCheck,
  ClipboardCheck,
  type LucideIcon,
} from "lucide-react";
import { REGULATORS } from "@/config/brand";

export const commitments = [
  {
    icon: CheckCircle2,
    stat: "Human",
    label: "Human-Reviewed Deliverables",
    description:
      "AI proposals require qualified human verification and approval; agents propose and cite, never decide.",
  },
  {
    icon: Brain,
    stat: "Governed",
    label: "Specialized AI Agents",
    description:
      "Purpose-built agents for investigations, complaints, contracts, inspections, lab investigations, QMS audits, supplier qualifications and change management — proposing and citing, never deciding.",
  },
  {
    icon: Clock,
    stat: "3-in-1",
    label: "Quality · Laboratory · Maintenance",
    description:
      "Linked eQMS, LIMS and CMMS records instead of three systems reconciled by spreadsheet.",
  },
  {
    icon: Shield,
    stat: "GxP",
    label: "Compliance by Design",
    description:
      "21 CFR Part 11 style e-signatures, ALCOA+ data integrity, append-only audit trails, and access control enforced in the database — not only the interface.",
  },
];

export type CustomerProfile = {
  icon: LucideIcon;
  title: string;
  description: string;
  scale: string;
  challenges: string;
  configuration: string;
  validation: string;
  rollout: string;
};

export const customers: CustomerProfile[] = [
  {
    icon: FlaskConical, title: "Emerging biotech",
    description: "Put a controlled quality foundation in place before the first GMP campaign or inspection.",
    scale: "Single site; a focused quality, CMC and laboratory team",
    challenges: "Paper or shared-drive SOPs, training tracked in spreadsheets, and outsourced manufacturing evidence spread across email.",
    configuration: "Document control, training, deviations and CAPA first, with QC Lab and asset records enabled as in-house activity grows.",
    validation: "Core validation package with IQ/OQ/PQ support for a single-site intended use.",
    rollout: "Phased go-live starting with controlled documents and training, then quality events.",
  },
  {
    icon: Building2, title: "Growth biopharma",
    description: "Connect quality, laboratory and equipment records as programs and departments multiply.",
    scale: "Several departments; tens to low hundreds of users",
    challenges: "Separate tools for quality, the lab and maintenance, reconciled manually before every review or audit.",
    configuration: "All three Spine pillars with cross-department workflows, role design and AI assistance enabled per team.",
    validation: "Expanded validation package covering the agreed departmental workflows.",
    rollout: "Department-by-department rollout with data migration planning and administrator training.",
  },
  {
    icon: Factory, title: "Multi-site mid-size",
    description: "Run shared quality practices with clear site-level ownership and one traceable record.",
    scale: "Multiple sites; hundreds of users",
    challenges: "Diverging site procedures, duplicated records and limited visibility of quality performance across locations.",
    configuration: "Site-aware roles and responsibilities, harmonized procedures, and Regulatory Watch feeding change control across sites.",
    validation: "Coordinated validation package with site-specific intended uses and phased qualification.",
    rollout: "Lead-site deployment followed by structured site waves under a joint governance plan.",
  },
  {
    icon: Users, title: "CDMO / CRO",
    description: "Keep client programs separated and evidence ready for every sponsor audit.",
    scale: "Service organizations with client-facing quality teams",
    challenges: "Many sponsors, many quality agreements and frequent audits, each needing a clean evidence trail.",
    configuration: "Program-level access boundaries, quality-agreement review, supplier qualification and complaint handling.",
    validation: "Validation package aligned to your service scope and sponsor expectations.",
    rollout: "Pilot with one client program, then expansion across service lines.",
  },
];

// Regulatory bodies displayed on the Solutions page — sourced from central
// brand config so every page agrees on the count and order.
export const regulatoryBodies = [
  ...REGULATORS.map((r) => ({ label: r.label, region: r.region })),
  { label: "Others", region: "Worldwide" },
];

const AGENT_ICONS: Record<string, LucideIcon> = {
  investigation: FileSearch,
  complaint: AlertTriangle,
  lab_investigation: FlaskConical,
  contract: FileText,
  inspection: TrendingUp,
  supplier_qualification: Truck,
  qms_audit: ShieldCheck,
  operational_excellence: ClipboardCheck,
};

import { AGENTS } from "@/config/brand";

export const specialistAgents: { icon: LucideIcon; name: string }[] = AGENTS.map((a) => ({
  icon: AGENT_ICONS[a.id] ?? FileText,
  name: a.name,
}));
